/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Dialog, Radio, RadioGroup, Typography2, breakpoints } from '@coursera/cds-core';

import { getLabeledActivityFilterOptions } from 'bundles/enterprise-admin-user-filters/constants';
import type { ActivityFilterType } from 'bundles/enterprise-admin-user-filters/types';
import { Spacer } from 'bundles/enterprise-ui';

import _t from 'i18n!nls/enterprise-admin-user-filters';

const { HeadingGroup, Content, Actions } = Dialog;

const ActivityFilterDialog = ({
  open,
  defaultValue,
  onClose,
  onSelect,
  ...rest
}: {
  defaultValue?: ActivityFilterType;
  open: boolean;
  onClose: () => void;
  onSelect: (filter: ActivityFilterType) => void;
}) => {
  const [filter, setFilter] = React.useState<ActivityFilterType | undefined>(defaultValue);
  return (
    <Dialog
      aria-describedby="activity-filter-dialog-content"
      open={open}
      variant="standard"
      sticky="actions"
      onClose={onClose}
      {...rest}
    >
      <HeadingGroup supportText={_t('Each filter has configurable options.')}>
        {_t('Select an activity filter to add')}
      </HeadingGroup>
      <Content id="activity-filter-dialog-content">
        <div css={{ background: 'var(--cds-color-grey-25)', padding: 'var(--cds-spacing-300)' }}>
          <Typography2 component="p">{_t('Select an activity filter to identify your learner cohort')}</Typography2>
          <Spacer size={12} />
          {getLabeledActivityFilterOptions().map(({ label, options }) => {
            return (
              <RadioGroup
                key={label + filter}
                value={filter}
                label={label}
                onChange={(event) => setFilter(event.target.value as ActivityFilterType)}
              >
                {options.map((props) => (
                  <Radio key={props.value} {...props} />
                ))}
              </RadioGroup>
            );
          })}
        </div>
      </Content>
      <Actions>
        <Button
          css={css`
            ${breakpoints.down('xs')} {
              width: 100%;
              max-width: none;
            }
          `}
          size="medium"
          variant="primary"
          disabled={!filter}
          onClick={() => filter && onSelect(filter)}
          data-testid="select-activity-filter"
        >
          {_t('Select')}
        </Button>
        <Button
          css={css`
            margin: 0 var(--cds-spacing-300) 0;
            ${breakpoints.down('xs')} {
              width: 100%;
              max-width: none;
              margin: var(--cds-spacing-300) 0 0;
            }
          `}
          size="medium"
          variant="secondary"
          onClick={onClose}
          data-testid="close-activity-filter-dialog"
        >
          {_t('Cancel')}
        </Button>
      </Actions>
    </Dialog>
  );
};
export default ActivityFilterDialog;
